import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SurviveMonthScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedAnswers, setSelectedAnswers] = useState({})

  const handleAnswer = (stepNum, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [stepNum]: answer })
  }

  // Calculate decision quality score
  const getDecisionQuality = () => {
    const correctAnswers = {
      4: 'B',  // Start with important expenses
      5: 'C',  // Fixed necessary costs
      6: 'B',  // Shopping with plan
      7: 'C',  // Discount trap
      8: 'B',  // Balance fun and responsibility
      9: 'C',  // Reserve for unexpected
      10: 'B'  // End-of-month reflection
    }
    
    let goodDecisions = 0
    Object.keys(correctAnswers).forEach(step => {
      if (selectedAnswers[step] === correctAnswers[step]) {
        goodDecisions++
      }
    })
    
    return goodDecisions
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-green-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">💰 Preží mesiac</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(10)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 10
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* INTRO SLIDE 1: What this story is about */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">📅</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  O čo pôjde v tomto príbehu?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">👨‍👩‍👧‍👦</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Pomôžeš rodine zvládnuť jeden mesiac
                      </p>
                      <p className="text-gray-600 text-sm">
                        Rodina štyroch ľudí má na mesiac 1200 € a musí rozhodnúť, na čo ich použiť
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💡</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Tvoja úloha
                      </p>
                      <p className="text-gray-600 text-sm">
                        Pomôžeš rozhodnúť, čo je dôležité, ako nakupovať múdro a ako sa pripraviť na nečakané situácie
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🎯</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Čo sa naučíš
                      </p>
                      <p className="text-gray-600 text-sm">
                        Zistíš, ako rozdeliť obmedzené peniaze tak, aby vydržali na dôležité veci aj na nečakané problémy
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať príbeh →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 2: What is a budget */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">📊</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Čo je rozpočet?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📝</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Rozpočet je plán
                      </p>
                      <p className="text-gray-600 text-sm">
                        Je to plán, ako rozdeliť peniaze počas určitého času (napríklad počas mesiaca)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 border-l-4 border-teal-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⚖️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Prečo je rozpočet dôležitý?
                      </p>
                      <p className="text-gray-600 text-sm">
                        Pomáha ti rozhodnúť, na čo použiješ peniaze, aby ti vydržali na všetko dôležité
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🎨</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Rozpočet neznamená nič nekupovať
                      </p>
                      <p className="text-gray-600 text-sm">
                        Rozpočet znamená rozumne vyberať, na čo použiješ obmedzené peniaze
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Pokračovať →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 3: What to watch out for */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Na čo si dávať pozor?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🏠</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Dôležité výdavky prichádzajú ako prvé
                      </p>
                      <p className="text-gray-600 text-sm">
                        Najprv treba zaplatiť nevyhnutné veci (bývanie, jedlo, doprava)
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💸</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Nemin všetko hneď na začiatku
                      </p>
                      <p className="text-gray-600 text-sm">
                        Peniaze musia vydržať celý mesiac, nie len prvý týždeň
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🛟</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Rezerva je užitočná
                      </p>
                      <p className="text-gray-600 text-sm">
                        Nechaj si trochu peňazí nabok, lebo môže prísť niečo nečakané
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-pink-50 border-l-4 border-pink-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🏷️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Pozor na zľavy a pokušenia
                      </p>
                      <p className="text-gray-600 text-sm">
                        Zľava nie je vždy dobrá, ak kúpiš niečo, čo momentálne nepotrebuješ
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať rozhodovať →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Beginning of month - how to start planning */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">📅</div>
                  <h2 className="text-2xl font-bold text-gray-900">Začiatok mesiaca</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-green-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodina má na tento mesiac <strong className="text-green-600">1200 €</strong>. Musí za ne zaplatiť všetko potrebné na celý mesiac.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Ako je najlepšie začať plánovať?
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je prvý rozumný krok?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(4, 'A')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Kúpiť hneď niečo príjemné, kým sú peniaze k dispozícii</div>
                  <div className="text-sm text-gray-600">Odmena na začiatku mesiaca</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'B')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Najprv si napísať dôležité výdavky a naplán ovať, koľko na čo pôjde</div>
                  <div className="text-sm text-gray-600">Začať s plánom</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'C')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Nechať všetky peniaze tak, ako sú, a minúť ich len keď bude treba</div>
                  <div className="text-sm text-gray-600">Žiadny plán, len reagovať</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'D')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Minúť hneď polovicu a zvyšok nechať na neskôr</div>
                  <div className="text-sm text-gray-600">Pol teraz, pol neskôr</div>
                </button>
              </div>

              {selectedAnswers[4] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[4] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[4] === 'B' ? '✅ Výborne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[4] === 'A' ? 'Kúpiť niečo príjemné hneď na začiatku nie je múdre. Peniaze musia vydržať celý mesiac a najprv treba zaplatiť dôležité veci.' :
                      selectedAnswers[4] === 'B' ? 'Presne tak! Dobrý rozpočet začína s plánom. Najprv si napíš dôležité výdavky a zisti, koľko približne na čo potrebuješ.' :
                      selectedAnswers[4] === 'C' ? 'Žiadny plán môže viesť k tomu, že peniaze rýchlo dôjdu a na konci mesiaca nezostane nič na dôležité veci.' :
                      'Minúť hneď polovicu bez plánu je riskantné. Nevieš, či ti tá polovica vystačí na všetky dôležité výdavky.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[4] === 'A' ? 'Dobrý rozpočet začína s plánom dôležitých výdavkov, nie s odmenami. Najprv treba zaplatiť nevyhnutné veci (bývanie, jedlo, doprava), potom až premýšľať o príjemných veciach.' :
                      selectedAnswers[4] === 'B' ? 'Rozumný začiatok je napísať si hlavné výdavky (bývanie, jedlo, doprava, škola) a naplánovať, koľko približne na čo pôjde. Tak vieš, koľko ti zostane na ostatné veci.' :
                      selectedAnswers[4] === 'C' ? 'Bez plánu je ľahké minúť peniaze rýchlo na náhodné veci. Potom môže chýbať na dôležité výdavky (jedlo, doprava). Plán pomáha rozdeliť peniaze múdro.' :
                      'Minúť hneď polovicu bez plánu je riskantné. Nevieš, či tá polovica (600 €) vystačí na všetky dôležité výdavky. Lepšie je najprv naplánovať, koľko na čo potrebuješ.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}


          {/* STEP 5: Fixed necessary costs */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🏠</div>
                  <h2 className="text-2xl font-bold text-gray-900">Pevné výdavky</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodina musí zaplatiť každý mesiac tieto veci:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Nájom bytu: 400 €</li>
                    <li>Jedlo (potraviny): približne 300 €</li>
                    <li>Doprava (MHD lístky, palivo): približne 100 €</li>
                    <li>Školské potreby pre deti: približne 50 €</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je múdre urobiť s týmito výdavkami?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(5, 'A')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Nechať to na neskôr, možno sa dá niečo z toho preskočiť</div>
                  <div className="text-sm text-gray-600">Možno to nebude potrebné</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'B')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Zaplatiť len časť a zvyšok nechať na koniec mesiaca</div>
                  <div className="text-sm text-gray-600">Po troške</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'C')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Odložiť si hneď peniaze na tieto nevyhnutné výdavky (850 €)</div>
                  <div className="text-sm text-gray-600">Dôležité veci najprv</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'D')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Minúť všetky peniaze na jedlo a zvyšok nejako vyriešiť</div>
                  <div className="text-sm text-gray-600">Jedlo je najdôležitejšie</div>
                </button>
              </div>

              {selectedAnswers[5] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[5] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[5] === 'C' ? '✅ Správne!' : '⚠️ Nie celkom!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[5] === 'A' ? 'Tieto výdavky sú nevyhnutné – nájom treba zaplatiť, rodina musí jesť a deti potrebujú chodiť do školy. Nedá sa to preskočiť.' :
                      selectedAnswers[5] === 'B' ? 'Nájom a základné výdavky treba zaplatiť celé a načas. Ak zaplatíš len časť, môžu vzniknúť problémy (penále, nedostatok jedla).' :
                      selectedAnswers[5] === 'C' ? 'Presne tak! Najprv odlož peniaze na nevyhnutné výdavky (850 €). Potom vieš, že máš na dôležité veci a zostáva ti 350 € na ostatné potreby.' :
                      'Aj keď jedlo je dôležité, rodina musí zaplatiť aj nájom, dopravu a školské potreby. Treba naplánovať všetky nevyhnutné výdavky.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[5] === 'A' ? 'Nájom, jedlo, doprava a školské potreby sú pevné nevyhnutné výdavky. Nedajú sa preskočiť. Dobrý rozpočet začína tým, že si odložíš peniaze na tieto veci.' :
                      selectedAnswers[5] === 'B' ? 'Nájom a základné výdavky treba zaplatiť celé a načas. Ak zaplatíš len časť, môžu vzniknúť penále (dodatočné poplatky) alebo problémy (vypnutý plyn, nedostatok jedla).' :
                      selectedAnswers[5] === 'C' ? 'Rozumné je hneď si odložiť peniaze na nevyhnutné výdavky (nájom 400 € + jedlo 300 € + doprava 100 € + škola 50 € = 850 €). Potom vieš, že máš na dôležité veci a zostáva ti 350 € na ostatné potreby a rezervu.' :
                      'Aj keď jedlo je veľmi dôležité, rodina musí zaplatiť aj nájom (400 €), dopravu (100 €) a školské potreby (50 €). Treba naplánovať všetky nevyhnutné výdavky, nie len jedlo.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 6: Shopping with plan */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🛒</div>
                  <h2 className="text-2xl font-bold text-gray-900">Týždenný nákup potravín</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Rodina ide nakúpiť potraviny na týždeň. Plánovala na to približne 75 €.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Ako je najlepšie nakupovať?
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Aký prístup je najrozumnejší?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(6, 'A')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Kúpiť čo sa páči, bez zoznamu a plánu</div>
                  <div className="text-sm text-gray-600">Spontánny nákup</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'B')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Napísať si zoznam potrebných vecí, porovnať ceny a držať sa plánu</div>
                  <div className="text-sm text-gray-600">Plánovaný nákup</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'C')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Kúpiť len najdrahšie značkové veci, lebo sú kvalitnejšie</div>
                  <div className="text-sm text-gray-600">Kvalita = drahé značky</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'D')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Kúpiť len to najlacnejšie, bez ohľadu na kvalitu</div>
                  <div className="text-sm text-gray-600">Len najlacnejšie</div>
                </button>
              </div>

              {selectedAnswers[6] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[6] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[6] === 'B' ? '✅ Výborne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[6] === 'A' ? 'Nákup bez zoznamu často vedie k tomu, že kúpiš veci, ktoré nepotrebuješ, a zabudneš na dôležité. To stojí viac peňazí.' :
                      selectedAnswers[6] === 'B' ? 'Presne tak! Zoznam pomáha nekúpiť zbytočnosti. Porovnanie cien pomáha ušetriť. Držať sa plánu znamená, že neutratíš viac, ako si plánoval.' :
                      selectedAnswers[6] === 'C' ? 'Najdrahšie značky nie sú vždy nutné. Často sú lacnejšie alternatívy rovnako dobré, len za nižšiu cenu.' :
                      'Len najlacnejšie veci môžu byť nízka kvalita. Lepšie je hľadať dobrý pomer ceny a kvality.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[6] === 'A' ? 'Spontánny nákup bez zoznamu často vedie k tomu, že kúpiš viac, ako potrebuješ, a zabudneš na dôležité veci. Potom musíš ísť znova do obchodu, čo stojí čas a peniaze.' :
                      selectedAnswers[6] === 'B' ? 'Zoznam potrebných vecí pomáha nekúpiť zbytočnosti. Porovnanie cien (napríklad medzi obchodmi alebo značkami) pomáha ušetriť. Držať sa plánu znamená, že neutratíš viac, ako si plánoval (napríklad 75 €).' :
                      selectedAnswers[6] === 'C' ? 'Najdrahšie značkové veci nie sú vždy nutné. Často sú lacnejšie alternatívy (obchodné značky) rovnako dobré, len za nižšiu cenu. Pri obmedzenom rozpočte je lepšie hľadať dobrú hodnotu, nie len značky.' :
                      'Len najlacnejšie veci môžu byť nízka kvalita a rýchlo sa pokaziť. Lepšie je hľadať dobrý pomer ceny a kvality – nie najdrahšie, ale ani najlacnejšie.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 7: Discount trap */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🏷️</div>
                  <h2 className="text-2xl font-bold text-gray-900">Lákavá zľava</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-orange-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    V obchode vidíš veľkú zľavu: <strong>"Televízor 50 % zľava! Iba 200 € namiesto 400 €!"</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Rodina má starý televízor, ktorý ešte funguje.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je rozumné rozhodnutie?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(7, 'A')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Kúpiť hneď, lebo 50 % zľava je skvelá príležitosť!</div>
                  <div className="text-sm text-gray-600">Zľava sa neopakuje</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'B')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Zľava znamená, že ušetríš peniaze, tak kúp</div>
                  <div className="text-sm text-gray-600">Zľava = úspora</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'C')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Starý televízor funguje, nový nie je nutný. Radšej nechať peniaze na dôležitejšie veci</div>
                  <div className="text-sm text-gray-600">Nepotrebné = zbytočný výdavok</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'D')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Kúpiť na splátky, tak nemusíš zaplatiť hneď</div>
                  <div className="text-sm text-gray-600">Splátky = žiadny problém</div>
                </button>
              </div>

              {selectedAnswers[7] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[7] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[7] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[7] === 'A' ? 'Zľava znie dobre, ale ak kúpiš niečo, čo nepotrebuješ, neutratíš menej – utratíš 200 €, ktoré si mohol ušetriť alebo použiť na dôležitejšie veci.' :
                      selectedAnswers[7] === 'B' ? 'Zľava nie je úspora, ak kúpiš niečo nepotrebné. Ušetriť znamená nezaplatiť nič, nie zaplatiť polovicu za vec, ktorú nepotrebuješ.' :
                      selectedAnswers[7] === 'C' ? 'Presne tak! Ak starý televízor funguje, nový nie je nutný. Tých 200 € môžeš použiť na dôležitejšie veci alebo si ich nechať ako rezervu.' :
                      'Splátky neznamenajú, že je nákup zadarmo. Stále platíš 200 € (+ možno úroky), len neskôr. Ak vec nie je nutná, splátky nevyriešia problém.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[7] === 'A' ? 'Zľava znie lákavo, ale ak kúpiš niečo, čo nepotrebuješ, neutratíš menej – utratíš 200 € (čo je veľa z rozpočtu 1200 €). Tie peniaze by bolo lepšie použiť na dôležitejšie veci alebo si ich nechať ako rezervu.' :
                      selectedAnswers[7] === 'B' ? 'Zľava nie je úspora, ak kúpiš niečo nepotrebné. Ušetriť znamená nezaplatiť nič (0 €), nie zaplatiť polovicu (200 €) za vec, ktorú nepotrebuješ. Ak starý televízor funguje, lepšie je nechať peniaze na dôležitejšie veci.' :
                      selectedAnswers[7] === 'C' ? 'Ak starý televízor funguje, nový nie je nutný. Tých 200 € môžeš použiť na dôležitejšie veci (napríklad rezervu na nečakané výdavky, školské potreby, jedlo). Dobrý rozpočet rozlišuje medzi potrebou a želaním.' :
                      'Splátky neznamenajú, že je nákup zadarmo. Stále platíš 200 € (+ možno úroky), len neskôr. Ak vec nie je nutná, splátky nevyriešia problém, len ho oddialila. Lepšie je nechať peniaze na dôležitejšie veci.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 8: Weekend fun */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎉</div>
                  <h2 className="text-2xl font-bold text-gray-900">Víkendová zábava</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Je víkend a deti by radi niečo podnikli. Máte niekoľko možností:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li>Kino pre celú rodinu: približne 40 €</li>
                    <li>Prechádzka v parku + zmrzlina: približne 10 €</li>
                    <li>Múzeum + obed vonku: približne 60 €</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je rozumný prístup?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(8, 'A')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Nič nekupovať, lebo rozpočet znamená šetriť všetko</div>
                  <div className="text-sm text-gray-600">Žiadna zábava</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'B')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Vybrať rozumnú aktivitu (napríklad park + zmrzlina), ktorá neprepáli rozpočet</div>
                  <div className="text-sm text-gray-600">Vyvážená zábava</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'C')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Urobiť všetko naraz (kino, múzeum, obed vonku), lebo rodina si to zaslúži</div>
                  <div className="text-sm text-gray-600">Všetko naraz</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'D')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Minúť všetky zostávajúce peniaze na zábavu</div>
                  <div className="text-sm text-gray-600">Vyčerpať rozpočet</div>
                </button>
              </div>

              {selectedAnswers[8] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[8] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[8] === 'B' ? '✅ Výborne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[8] === 'A' ? 'Rozpočet neznamená nič nekupovať. Znamená rozumne vyberať. Trochu zábavy je OK, ak je v rozumných medziach.' :
                      selectedAnswers[8] === 'B' ? 'Presne tak! Dobrý rozpočet neznamená bez zábavy. Znamená vybrať aktivitu, ktorá neprehorí rozpočet (10 € je rozumné).' :
                      selectedAnswers[8] === 'C' ? 'Všetko naraz (kino + múzeum + obed = 100 €+) je príliš veľa z rozpočtu. Lepšie je vybrať jednu alebo dve veci rozumne.' :
                      'Minúť všetky zostávajúce peniaze na zábavu je riskantné. Čo ak príde nečakaný výdavok? Vždy treba nechať nejakú rezervu.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[8] === 'A' ? 'Rozpočet neznamená nič nekupovať a žiť bez zábavy. Znamená rozumne vyberať a nenakúpiť zbytočnosti. Trochu zábavy (napríklad 10 € na park a zmrzlinu) je OK a zdravé.' :
                      selectedAnswers[8] === 'B' ? 'Dobrý rozpočet neznamená žiť bez zábavy. Znamená vybrať aktivitu, ktorá je príjemná, ale neprehorí rozpočet. Park a zmrzlina za 10 € je rozumná voľba – rodina sa zabáva, ale nezostane bez peňazí.' :
                      selectedAnswers[8] === 'C' ? 'Všetko naraz (kino 40 € + múzeum 60 € = 100 €+) je príliš veľa z rozpočtu 1200 €. Lepšie je vybrať jednu alebo dve veci rozumne a nechať peniaze na ostatné potreby a rezervu.' :
                      'Minúť všetky zostávajúce peniaze na zábavu je veľmi riskantné. Čo ak príde nečakaný výdavok (oprava, lekár)? Vždy treba nechať nejakú rezervu na nečakané situácie.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 9: Unexpected expense */}
          {currentStep === 9 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🚨</div>
                  <h2 className="text-2xl font-bold text-gray-900">Nečakaný problém</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-red-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Koniec mesiaca. Náhle sa pokazil bojler a treba ho opraviť. Oprava stojí <strong>80 €</strong>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Rodina potrebuje teplú vodu, oprava je nutná.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je najrozumnejšie urobiť?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(9, 'A')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Odložiť opravu, lebo nie sú peniaze</div>
                  <div className="text-sm text-gray-600">Počkať na budúci mesiac</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'B')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Požičať si od suseda alebo rodiny</div>
                  <div className="text-sm text-gray-600">Požičať peniaze</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'C')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Použiť rezervu, ktorú si rodina nechala na nečakané situácie</div>
                  <div className="text-sm text-gray-600">Rezerva je na toto</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'D')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nejesť niekoľko dní, aby sa ušetrilo na opravu</div>
                  <div className="text-sm text-gray-600">Šetriť na jedle</div>
                </button>
              </div>

              {selectedAnswers[9] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[9] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[9] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[9] === 'A' ? 'Odložiť opravu bojlera nie je múdre. Rodina potrebuje teplú vodu. Preto je dôležité mať rezervu na nečakané situácie.' :
                      selectedAnswers[9] === 'B' ? 'Požičať si môže byť riešenie, ale je lepšie mať vlastnú rezervu. Preto je dobrý rozpočet nechať si trochu peňazí nabok.' :
                      selectedAnswers[9] === 'C' ? 'Presne tak! Preto je rezerva dôležitá. Keď príde nečakaný problém (oprava, lekár), rezerva pomôže vyriešiť situáciu bez paniky.' :
                      'Šetriť na jedle nie je zdravé riešenie. Rodina musí jesť. Preto je dôležité mať rezervu na nečakané výdavky.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[9] === 'A' ? 'Odložiť opravu bojlera znamená, že rodina nebude mať teplú vodu, čo je problém. Preto je dôležité mať rezervu – peniaze odložené na nečakané situácie (opravy, lekár, nečakané výdavky).' :
                      selectedAnswers[9] === 'B' ? 'Požičať si môže byť núdzové riešenie, ale musíš to vrátiť, čo môže byť problém v budúcnosti. Preto je lepšie mať vlastnú rezervu – peniaze odložené na nečakané situácie.' :
                      selectedAnswers[9] === 'C' ? 'Rezerva je peniaze odložené presne na takéto nečakané situácie (opravy, lekár, nečakané výdavky). Ak si rodina nechala nabok 80–100 €, môže zaplatiť opravu bez paniky a bez požičiavania. Preto je rezerva dôležitá časť dobrého rozpočtu.' :
                      'Šetriť na jedle nie je zdravé ani bezpečné riešenie. Rodina musí jesť. Preto je dôležité mať rezervu – peniaze odložené na nečakané situácie, aby si nemusel obetovať jedlo alebo iné dôležité veci.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 10: End-of-month reflection with quality-based feedback */}
          {currentStep === 10 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎯</div>
                  <h2 className="text-2xl font-bold text-gray-900">Koniec mesiaca</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Mesiac sa skončil. Je čas zhodnotiť, ako sa rodine podarilo zvládnuť rozpočet.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Čo bolo najdôležitejšie pri plánovaní mesiaca?
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Aké je hlavné ponaučenie z tohto mesiaca?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(10, 'A')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Dobrý rozpočet znamená nič nekupovať a len šetriť</div>
                  <div className="text-sm text-gray-600">Šetriť všetko</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'B')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Dobrý rozpočet znamená prioritizovať dôležité veci, plánovať a nechať rezervu</div>
                  <div className="text-sm text-gray-600">Plán + priority + rezerva</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'C')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Dobrý rozpočet znamená využiť všetky zľavy</div>
                  <div className="text-sm text-gray-600">Zľavy sú kľúč</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'D')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Dobrý rozpočet znamená minúť všetko hneď na začiatku</div>
                  <div className="text-sm text-gray-600">Všetko hneď</div>
                </button>
              </div>

              {selectedAnswers[10] && (
                <div>
                  <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                    selectedAnswers[10] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                  }`}>
                    <p className="font-semibold text-gray-800 mb-2">
                      {selectedAnswers[10] === 'B' ? '✅ Výborne!' : '⚠️ Pozor!'}
                    </p>
                    <p className="text-gray-700 text-sm mb-3">
                      <strong>Tip:</strong> {
                        selectedAnswers[10] === 'A' ? 'Rozpočet neznamená nič nekupovať. Znamená rozumne vyberať a prioritizovať dôležité veci.' :
                        selectedAnswers[10] === 'B' ? 'Presne tak! Dobrý rozpočet znamená najprv zaplatiť dôležité veci, plánovať nákupy a nechať trochu rezervy na nečakané situácie.' :
                        selectedAnswers[10] === 'C' ? 'Zľavy nie sú automaticky dobré. Ak kúpiš nepotrebné veci len kvôli zľave, neutratíš menej, ale utratíš peniaze zbytočne.' :
                        'Minúť všetko hneď znamená, že nezostane nič na koniec mesiaca. Čo ak príde nečakaný problém? Dobrý rozpočet znamená plánovať na celý mesiac.'
                      }
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Vysvetlenie:</strong> {
                        selectedAnswers[10] === 'A' ? 'Rozpočet neznamená nič nekupovať a žiť bez zábavy. Znamená rozumne vyberať – prioritizovať dôležité veci (bývanie, jedlo, doprava), plánovať nákupy a nechať rezervu na nečakané situácie.' :
                        selectedAnswers[10] === 'B' ? 'Dobrý rozpočet má tri kľúčové časti: 1) Prioritizovať dôležité výdavky (najprv zaplatiť nevyhnutné veci), 2) Plánovať nákupy (zoznam, porovnanie cien), 3) Nechať rezervu (na nečakané výdavky ako opravy). Rozpočet znamená rozumne vyberať, nie nič nekupovať.' :
                        selectedAnswers[10] === 'C' ? 'Zľavy nie sú automaticky dobré. Ak kúpiš nepotrebné veci len kvôli zľave, neutratíš menej – utratíš peniaze zbytočne. Dobrý rozpočet znamená kupovať to, čo potrebuješ, nie to, čo je v zľave.' :
                        'Minúť všetko hneď na začiatku mesiaca znamená, že nezostane nič na koniec. Čo ak príde nečakaný problém (oprava, lekár)? Dobrý rozpočet znamená plánovať na celý mesiac a nechať rezervu.'
                      }
                    </p>
                  </div>

                  {/* Quality-based final reflection */}
                  <div className="mt-8 p-8 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border-2 border-green-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">📝</span>
                      Tvoje rozhodovanie
                    </h3>
                    
                    {(() => {
                      const score = getDecisionQuality()
                      
                      if (score >= 6) {
                        return (
                          <div className="space-y-3">
                            <p className="text-green-700 font-semibold">
                              ✅ Výborne! Rozhodoval si veľmi múdro.
                            </p>
                            <p className="text-gray-700">
                              Pochopil si, že <strong>dobrý rozpočet začína s plánom</strong> a prioritizovaním dôležitých výdavkov. Uvedomil si, že <strong>nie každá zľava je dobrá</strong>, ak kúpiš niečo nepotrebné.
                            </p>
                            <p className="text-gray-700">
                              Dokázal si <strong>plánovať nákupy s rozumom</strong> (zoznam, porovnanie cien) a pochopil si, že <strong>rozpočet neznamená nič nekupovať</strong>, ale rozumne vyberať. Najdôležitejšie: Uvedomil si si, že <strong>rezerva je užitočná</strong> na nečakané situácie.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Záver:</strong> Múdre rozdeľovanie peňazí znamená prioritizovať dôležité veci, plánovať nákupy a nechať trochu rezervy. Tak peniaze vydržia celý mesiac aj na nečakané situácie.
                            </p>
                          </div>
                        )
                      } else if (score >= 4) {
                        return (
                          <div className="space-y-3">
                            <p className="text-yellow-700 font-semibold">
                              ⚠️ Niektoré rozhodnutia boli dobré, iné menej.
                            </p>
                            <p className="text-gray-700">
                              Niekedy si si uvedomil, že <strong>dôležité výdavky prichádzajú ako prvé</strong> a že <strong>plánovanie pomáha</strong>. To je dobré!
                            </p>
                            <p className="text-gray-700">
                              Ale niekoľkokrát si <strong>podcenil dôležitosť rezervy</strong> alebo si <strong>minul peniaze na nepotrebné veci</strong> (napríklad zľavy na veci, ktoré nie sú nutné). To môže spôsobiť problémy, keď príde nečakaný výdavok.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo si zapamätať:</strong> Rozpočet znamená najprv zaplatiť dôležité veci, plánovať nákupy a nechať rezervu. Zľava nie je vždy dobrá, ak kúpiš niečo nepotrebné.
                            </p>
                          </div>
                        )
                      } else {
                        return (
                          <div className="space-y-3">
                            <p className="text-red-700 font-semibold">
                              ⚠️ Tvoje rozhodovanie ignorovalo dôležité princípy rozpočtu.
                            </p>
                            <p className="text-gray-700">
                              Niekoľkokrát si <strong>minul peniaze bez plánu</strong>, <strong>kúpil nepotrebné veci len kvôli zľave</strong> alebo <strong>nezanechal rezervu</strong> na nečakané situácie. To môže viesť k tomu, že peniaze rýchlo dôjdu a na konci mesiaca nezostane nič na dôležité veci.
                            </p>
                            <p className="text-gray-700">
                              Možno si tiež <strong>podcenil dôležitosť plánovania</strong> – bez plánu je ľahké minúť peniaze rýchlo a potom chýbajú na nevyhnutné výdavky (jedlo, doprava).
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo by si mal vedieť:</strong> Dobrý rozpočet má tri kľúčové časti: 1) Prioritizovať dôležité výdavky (najprv zaplatiť nevyhnutné veci), 2) Plánovať nákupy (zoznam, porovnanie cien, vyhýbať sa nepotrebným veciam), 3) Nechať rezervu (na nečakané výdavky ako opravy).
                            </p>
                            <p className="text-gray-700">
                              <strong>Zapamätaj si:</strong> Rozpočet neznamená nič nekupovať. Znamená rozumne vyberať – prioritizovať dôležité veci, plánovať a nechať trochu rezervy na nečakané situácie. Tak peniaze vydržia celý mesiac.
                            </p>
                          </div>
                        )
                      }
                    })()}
                  </div>

                  <div className="mt-8 flex justify-center gap-4">
                    <button
                      onClick={() => navigate('/scenarios/8')}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                    >
                      Späť na scenáre
                    </button>
                    <button
                      onClick={() => {
                        setCurrentStep(1)
                        setSelectedAnswers({})
                      }}
                      className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Skúsiť znova
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Continue with remaining steps... Due to length, I'll create the file with all steps */}

        </div>
      </div>
    </div>
  )
}

export default SurviveMonthScenario
